<template>
    <div
        @mouseenter="onInternalEnter" 
        @mouseleave="onInternalLeave"
    >
        <div class="modal-title">
            <!-- Accessing title from the dictionary object -->
            <h2>{{ dataDictionary.title || 'Schedule Meeting' }}</h2>
        </div>

        <div class="appointment-content">
            <form @submit.prevent="handleSubmit">
                <div class="section-label">Choose two meeting options</div>
                <fieldset class="appointment-choice">
                    <legend>First choice</legend>
                    <label>
                        Date
                        <input
                            type="date"
                            v-model="form.date1"
                            @click="openPicker"
                            :disabled="submitting"
                            required
                        />
                    </label>
                    <label>
                        Time
                        <input
                            type="time"
                            v-model="form.time1"
                            @click="openPicker"
                            :disabled="submitting"
                            required
                        />
                    </label>
                </fieldset>
                <fieldset class="appointment-choice">
                    <legend>Second choice</legend>
                    <label>
                        Date
                        <input
                            type="date"
                            v-model="form.date2"
                            @click="openPicker"
                            :disabled="submitting"
                            required
                        />
                    </label>
                    <label>
                        Time
                        <input
                            type="time"
                            v-model="form.time2"
                            @click="openPicker"
                            :disabled="submitting"
                            required
                        />
                    </label>
                </fieldset>


                <!-- 2. Meeting Type (Chips) -->
                <div class="section-label">
                    Meeting Topic <span aria-hidden="true">*</span>
                    <span class="sr-only"> (required)</span>
                </div>
                <div class="chips-container" role="group" aria-label="Meeting topic" aria-required="true">
                    <button 
                    class="chip"
                        v-for="type in meetingTypes" 
                        :key="type"
                        type="button"
                        :class="{ active: form.type === type }"
                        :aria-pressed="form.type === type"
                        @click="form.type = type"
                        :disabled="submitting"
                    >
                        {{ type }}
                    </button>
                </div>

                <!-- 3. Details -->
                <textarea 
                    v-model="form.details" 
                    maxlength="300"
                    rows="4"
                    placeholder="What is this regarding?"
                    :disabled="submitting"
                ></textarea>

                <input
                    type="email"
                    v-model="form.email"
                    placeholder="Provide an email we can respond to"
                    :disabled="submitting"
                    required
                />

                <button type="submit" :disabled="submitting">
                    {{ submitting ? 'Submitting…' : 'Submit Request' }}
                </button>
            </form>
            <p class="appointment-status" aria-live="polite">{{ status }}</p>
        </div>
    </div>
</template>

<script>
import { toast } from '@/utilities/toaster';

const APPOINTMENT_ENDPOINT = 'https://formsubmit.co/ajax/d7a1ab1e3c506057146c9461a0adf40e';

export default {
    name: "AppointmentChild",
    props: {
        // Change type to Object unless you are specifically passing a JS new Map()
        dataDictionary: {
            type: Object,
            required: true,
            default: () => ({ title: "Pi Teams" })
        }
    },
    data() {
        return {
            meetingTypes: this.dataDictionary.meetingTypes,
            form: {
                date1: '',
                time1: '',
                date2: '',
                time2: '',
                type: this.dataDictionary?.meetingTypes[0] || "",
                details: '',
                email: ''
            },
            submitting: false,
            status: ''
        }
    },
    methods: {
        openPicker(event) {
            if (typeof event.currentTarget.showPicker === 'function') {
                event.currentTarget.showPicker();
            }
        },
        async handleSubmit() {
            if (this.submitting) return;
            if (!this.form.type) {
                this.status = 'Please select a meeting topic.';
                toast.error(this.status);
                return;
            }

            this.submitting = true;
            this.status = '';

            try {
                const booking = {
                    ...this.form,
                    date1: `${this.form.date1}T${this.form.time1}`,
                    date2: `${this.form.date2}T${this.form.time2}`
                };
                const response = await fetch(APPOINTMENT_ENDPOINT, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        _subject: 'New appointment booking request',
                        preferred_date_1: booking.date1,
                        preferred_date_2: booking.date2,
                        meeting_topic: booking.type,
                        details: booking.details,
                        reply_email: booking.email
                    })
                });

                const result = await response.json();
                if (!response.ok || (result.success !== 'true' && result.success !== true)) {
                    throw new Error(result.message || `Booking request failed with status ${response.status}`);
                }

                this.status = 'Your booking request has been sent. We will be in touch soon.';
                toast.success(this.status);
                this.$emit('submit-appointment', booking);
                this.form.date1 = '';
                this.form.time1 = '';
                this.form.date2 = '';
                this.form.time2 = '';
                this.form.details = '';
                this.form.email = '';
            } catch (error) {
                this.status = 'We could not submit your booking. Please try again later.';
                toast.error(this.status);
                console.error('Appointment booking failed.', error);
            } finally {
                this.submitting = false;
            }
        },
        onInternalEnter() {
            this.$emit('mouseenter-child', this.$options.name);
        },
        onInternalLeave() {
            this.$emit('mouseleave-child');
        }

    }
}
</script>

<style scoped>
.appointment-choice {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 1rem;
    margin: 0 0 1rem;
    padding: 0.75rem;
    border: 1px solid currentColor;
    border-radius: 0.5rem;
}

.appointment-choice legend {
    padding: 0 0.35rem;
}

.appointment-choice label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.appointment-choice input {
    box-sizing: border-box;
    width: 100%;
    cursor: pointer;
}

.appointment-status {
    min-height: 1.4em;
}

.appointment-content button:disabled,
.appointment-content input:disabled,
.appointment-content textarea:disabled {
    cursor: wait;
    opacity: 0.7;
}

@media (max-width: 480px) {
    .appointment-choice {
        grid-template-columns: 1fr;
    }
}
</style>
